// 計分、課表推導、能量估算、趨勢分析等純邏輯都在 src/domain/，由特徵測試直接覆蓋。
// 畫面全部在 src/ui/App.tsx。這個檔案只負責取資料、持有狀態、接線。
import { h, render } from "preact";
import { App } from "./ui/App.js";
import { PLAN_START, PLAN_END, clampToPlan } from "./domain/plan.js";
import { seasonBaseline } from "./domain/measurements.js";
import { todayIso } from "./domain/dates.js";
import { num, csvEscape } from "./domain/numbers.js";
import { normalizeProfile } from "./domain/energy.js";
import { measurementView } from "./domain/measurement-view.js";
import { summaryText as summaryTextFor } from "./domain/summary.js";
import { createDailyLogStore } from "./data/daily-log-store.js";
import { createCheerStore } from "./data/cheer-store.js";
import { createRacePredictionStore } from "./data/race-prediction-store.js";
import { FINISH_RANGE_MESSAGE, parseFinishTime, validFinishSeconds } from "../shared/race-prediction.js";
import { SEASON_1 } from "../shared/plan.js";
import {
  loadEnergyProfile, saveEnergyProfile,
  npcLineOffset as readNpcLineOffset, setNpcLineOffset as writeNpcLineOffset
} from "./storage/profile.js";
import { loadNickname, saveNickname } from "./storage/nickname.js";
import { dismissedNoticeId, dismissNotice } from "./storage/notice.js";
import { dismissInstallHint, installHintDismissed } from "./storage/install-hint.js";
import { saveSeenReveals, seenReveals } from "./storage/reveal-seen.js";
import { externalBrowserUrl, installHint } from "./domain/install-hint.js";
import { NOTICE, noticeToShow, hasNewBuild } from "./domain/notice.js";
import { normalizeNickname } from "../shared/nickname.js";
import { badgeStatus, setBadgeEnabled, syncAppBadge } from "./storage/app-badge.js";
import { dayCompletion } from "./domain/scoring.js";
import { can } from "../shared/roles.js";

// 回到前景時的最短重抓間隔。刻意設短——「切去跑捷徑再切回來」是這個
// 機制主要要服務的情境，間隔太長會讓它在該情境失效。
const FOREGROUND_REFRESH_INTERVAL = 10_000;
const TOAST_MS = 2200;

const BOOL_FIELDS = [
  "workout_done", "protein_breakfast_done", "protein_lunch_done",
  "protein_dinner_done", "vegetables_done", "water_done", "supplements_done"
];

const root = document.getElementById("app");
const planDate = () => clampToPlan(todayIso());

function buildInfoText() {
  const builtAt = new Intl.DateTimeFormat("zh-TW", {
    timeZone: "Asia/Taipei", year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false
  }).format(new Date(__BUILD_TIME__));
  // 版號答「這是哪一版」，commit 短碼答「CHANGELOG 的哪一段」，
  // 建置時間答「夠不夠新」——三個問題各自要不同的東西，所以三個都留。
  return `v${__APP_VERSION__}・${__BUILD_COMMIT__}・建置於 ${builtAt}`;
}

/**
 * 從主畫面開啟的 PWA。需要重裝的公告只給這些人看——用瀏覽器逛的人
 * 看到「請重新加入主畫面」只會困惑。
 */
function isStandalone() {
  return matchMedia("(display-mode: standalone)").matches || navigator.standalone === true;
}

function currentNotice(dismissedId) {
  return noticeToShow(NOTICE, { dismissedId, standalone: isStandalone() });
}

/**
 * 線上是不是有比手上這份新的建置。
 *
 * iOS 從主畫面開 PWA 是恢復凍結的頁面而不是重新載入，所以只要使用者沒把
 * App 滑掉，部署了新版他仍然在跑舊的 JS——資料會更新，程式碼不會。
 *
 * no-store：這支的整個用途就是問「線上現在是哪一版」，拿到快取的等於沒問。
 * 失敗（離線、檔案掛掉）一律當成沒有新版，不要跳一條點了也沒用的提示。
 */
async function checkForUpdate() {
  try {
    const res = await fetch("/version.json", { cache: "no-store" });
    if (!res.ok) return;
    const { commit } = await res.json();
    if (hasNewBuild(__BUILD_COMMIT__, commit)) setUi({ updateAvailable: true });
  } catch {
    /* 離線就安靜 */
  }
}

// logs / cheers / racePredictions 都由各自的 store 擁有，不在 state 裡——
// 樂觀寫入與回滾的完整生命週期都在那些 module 裡面。
const state = {
  /**
   * 量測資料的視圖（`src/domain/measurement-view.js`）。
   *
   * **遮蔽與否由伺服器決定，這裡只是把回應轉成 view**——前端自己判斷日期的話，
   * 改系統時鐘就提前開獎。轉成 view 之後「這是公斤還是無單位的 0–1」就寫在
   * 型別裡，不再是一個要每個呼叫端記得去看的旗標。
   *
   * 初始值是 `revealed` ＋ 空陣列：所有函式在空資料下本來就回 null／「—」，
   * 與登入前的畫面一致。
   */
  view: measurementView([], false, null),
  /** 第一季的體重，賽季起點與舊競猜的結算用。還沒揭曉時留空，見 loadAll()。 */
  season1Measurements: [],
  /** 生日賽季的競猜，已封盤，只讀。 */
  predictions: [],
  /** 官方完賽成績。還沒補登是 null。 */
  raceResult: null,
  /** 第一季的紀錄，賽季成績單用。已封存不會再變，所以只抓一次，見 loadSeason1()。 */
  season1Logs: {},
  season1HealthMetrics: {},
  season1Loaded: false,
  healthMetrics: {},
  profile: loadEnergyProfile(),
  /**
   * "owner" / "viewer" / null（未登入）。取代先前的 authenticated 布林——
   * 能不能做某件事一律問 shared/roles.js 的 can()，不要在這裡展開成一堆布林。
   */
  role: null,
  loading: false,
  lastLoadedAt: 0,
  ui: {
    activeTab: "today",
    loginVisible: false,
    loginError: "",
    toast: "",
    toastVisible: false,
    refreshing: false,
    failedLoads: [],
    calendarMonth: planDate().slice(0, 7),
    selectedDate: planDate(),
    npcOffset: readNpcLineOffset(planDate()),
    nickname: loadNickname(),
    nicknameError: "",
    predictionError: "",
    raceResultError: "",
    openAchievement: null,
    notice: currentNotice(dismissedNoticeId()),
    installHint: null,
    seenReveals: seenReveals(),
    externalBrowserHref: externalBrowserUrl(location.href),
    updateAvailable: false,
    mascotBouncing: false,
    cheerBouncing: false,
    syncStatus: undefined,
    syncError: undefined,
    syncEndpoint: `${location.origin}/api/health-sync`,
    buildInfo: buildInfoText(),
    openHelp: null,
    /*
     * 目前展開的補登面板，`null` 代表全部收起來。
     *
     * **預設收起來**：補登是捷徑壞掉時的備援，不是每天都要看的東西，而那
     * 兩塊輸入區攤開來會把當天的數字擠到螢幕外。
     *
     * 一次只開一個，與 openHelp／openAchievement 同一個形狀——`date` 一併記
     * 是因為 DayEditor 會同時渲染兩次，只記 kind 的話兩邊會一起展開。
     */
    openBackfill: null,
    badge: badgeStatus(),
    /*
     * 手動補登的草稿，步數與身體數值**各自一份**。
     *
     * 合成一份共用 `date` 的話，在同一天打了體重再去存步數，存完的重置會把
     * 還沒送出的體重清掉——而使用者只會看到自己打的字消失。
     *
     * 各自的 `date` 是「正在編輯哪一天」，不可省：DayEditor 會同時渲染兩次
     * （今日與選取日），沒有它的話在一格打字會兩格一起變。
     *
     * 值一律存字串——輸入到一半的 "8" 不該被當成 8 步，清空的 "" 也不該變成
     * 0。轉數字只在送出那一刻做一次。
     */
    backfill: {
      steps: { date: null, value: "", status: "idle", error: "" },
      // 淨體重與 BMI 的空字串代表「用自動算的」，不是「清成 null」。
      // 體重與體脂的空字串才是「清掉這一格」。見 ui/DayEditor.tsx。
      body: {
        date: null, weight: "", fat: "", lean: "", bmi: "",
        status: "idle", error: ""
      }
    }
  }
};

/**
 * 任務角標要顯示的數字：今天還沒完成的核心任務數。
 *
 * 用 todayIso() 而不是 planDate()——planDate() 會把日期夾進計畫期間，
 * 生日之後每天都會夾回 11/04，角標就會一直掛著那天的剩餘數。
 * 計畫期間之外回 0，等於清掉角標。
 */
function badgeRemaining() {
  const today = todayIso();
  if (today < PLAN_START || today > PLAN_END) return 0;
  const progress = dayCompletion(dailyLogs.logs[today], state.healthMetrics[today]);
  return progress.total - progress.done;
}

function rerender() {
  render(h(App, {
    data: {
      measurements: state.view,
      season1Measurements: state.season1Measurements,
      logs: dailyLogs.logs,
      healthMetrics: state.healthMetrics,
      cheers: cheers.cheers,
      predictions: state.predictions,
      racePredictions: racePredictions.predictions,
      raceResult: state.raceResult,
      season1Logs: state.season1Logs,
      season1HealthMetrics: state.season1HealthMetrics
    },
    today: todayIso(),
    planDate: planDate(),
    profile: state.profile,
    // 未登入時先當成監督者渲染——遮罩蓋著看不到，但底下不會是可編輯的介面
    role: state.role || "viewer",
    ui: state.ui,
    actions
  }), root);

  // 角標是完成度的另一種呈現，所以綁在重繪之後同步——只要畫面上的
  // 「n / 5 完成」變了角標就跟著變，兩者不可能講不同的話。
  // 監督者不掛角標：那是「我今天還剩幾項」，掛在別人的手機上沒有意義。
  if (state.role === "owner") syncAppBadge(badgeRemaining());
}

/** 更新 UI 狀態並重繪。所有畫面變化都走這裡。 */
function setUi(patch) {
  Object.assign(state.ui, patch);
  rerender();
}

let toastTimer;
function toast(message) {
  setUi({ toast: message, toastVisible: true });
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => setUi({ toastVisible: false }), TOAST_MS);
}

async function api(url, options = {}) {
  const res = await fetch(url, {
    credentials: "same-origin",
    headers: { ...(options.body ? { "content-type": "application/json" } : {}), ...(options.headers || {}) },
    ...options
  });
  let data = {};
  try { data = await res.json(); } catch { /* 非 JSON 回應由下面的狀態碼處理 */ }
  if (res.status === 401) { setUi({ loginVisible: true }); throw new Error(data.error || "尚未登入"); }
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}

// logs 由 store 擁有——樂觀寫入與回滾的完整生命週期都在它裡面
const dailyLogs = createDailyLogStore({
  writer: {
    async save(date, patch) {
      const data = await api("/api/daily-logs", {
        method: "PUT",
        body: JSON.stringify({ log_date: date, ...patch })
      });
      return normalizeLog(data.log);
    }
  },
  onChange: rerender,
  onError: toast
});

// 戳與競猜同一條 seam，只是形狀不同：戳是新增一列，競猜是覆蓋一列。
// 兩者都回傳結果而不是自己回報——失敗的說明一個進 toast、一個進畫面上的
// 錯誤區，該放哪裡是呼叫端的事。
const cheers = createCheerStore({
  writer: {
    save(date, nickname) {
      return api("/api/cheers", {
        method: "POST",
        body: JSON.stringify({ cheer_date: date, nickname })
      });
    }
  },
  onChange: rerender
});

const racePredictions = createRacePredictionStore({
  writer: {
    save(nickname, { willFinish, willReachWeight, weightKg, finishSeconds }) {
      return api("/api/race-predictions", {
        method: "POST",
        body: JSON.stringify({
          nickname,
          will_finish: willFinish,
          will_reach_weight: willReachWeight,
          weight_kg: weightKg,
          finish_seconds: finishSeconds
        })
      });
    }
  },
  onChange: rerender
});

function normalizeMeasurement(row) {
  return {
    ...row,
    weight_kg: num(row.weight_kg),
    body_fat_percent: num(row.body_fat_percent),
    lean_body_mass_kg: num(row.lean_body_mass_kg),
    bmi: num(row.bmi)
  };
}

function normalizeLog(row) {
  const out = { ...row };
  for (const field of BOOL_FIELDS) out[field] = Number(out[field]) === 1;
  return out;
}

function normalizeHealthMetric(row) {
  return { ...row, steps_count: num(row.steps_count) };
}

/**
 * 我是誰。
 *
 * 登入端點刻意不在回應裡透露命中的是哪一組密碼，所以角色一律從這支問——
 * 登入成功之後也要再問一次。
 */
async function readRole() {
  const res = await fetch("/api/auth/session", { credentials: "same-origin" });
  const data = await res.json();
  state.role = data.role || null;
  // 等角色確定才決定，否則本人登入時會閃一下給監督者看的提示
  state.ui.installHint = currentInstallHint();
  return state.role;
}

/** 加入主畫面的提示只給監督者。本人早就裝好了。 */
function currentInstallHint() {
  if (state.role !== "viewer" || installHintDismissed()) return null;
  return installHint({
    userAgent: navigator.userAgent,
    maxTouchPoints: navigator.maxTouchPoints,
    standalone: isStandalone()
  });
}

async function checkSession() {
  if (await readRole()) {
    setUi({ loginVisible: false });
    await loadAll();
  } else {
    setUi({ loginVisible: true });
  }
}

/** 首頁要抓的每一支：名稱（失敗時畫面與提示用）、網址、拿到之後寫進哪裡。 */
function dataSources() {
  const season2 = `start=${PLAN_START}&end=${PLAN_END}`;
  return [
    ["measurements", "/api/measurements?season=2", m => {
      state.view = measurementView(
        (m.measurements || []).map(normalizeMeasurement),
        m.redacted === true,
        m.directions || null
      );
    }],
    ["season1Measurements", "/api/measurements?season=1", s1 => {
      // 伺服器說還沒揭曉就留空：無單位的 0–1 不能拿去當體重起點或結算
      state.season1Measurements = s1.redacted === true ? [] : (s1.measurements || []).map(normalizeMeasurement);
    }],
    ["logs", `/api/daily-logs?${season2}`, l => {
      dailyLogs.replaceAll(Object.fromEntries((l.logs || []).map(row => [row.log_date, normalizeLog(row)])));
    }],
    ["health", `/api/daily-health?${season2}`, h => {
      state.healthMetrics = Object.fromEntries((h.metrics || []).map(row => [row.log_date, normalizeHealthMetric(row)]));
    }],
    ["cheers", `/api/cheers?${season2}`, c => { cheers.replaceAll(c.cheers || []); }],
    ["predictions", "/api/predictions", p => { state.predictions = p.predictions || []; }],
    ["racePredictions", "/api/race-predictions", rp => { racePredictions.replaceAll(rp.predictions || []); }],
    ["raceResult", "/api/race-result", rr => { state.raceResult = rr.result || null; }]
  ];
}

// silent：自動觸發的重抓不出聲，成功與失敗都不跳 toast。
// 手動按 ↻ 需要明確回饋，所以預設會提示。
async function loadAll({ silent = false } = {}) {
  if (state.loading) return;
  state.loading = true;
  state.lastLoadedAt = Date.now();
  setUi({ refreshing: true });

  // 一支壞掉不拖累其他支。全有全無的話，一支 500 就會讓整個成績單變空白
  const failed = [];
  await Promise.all(dataSources().map(async ([name, url, apply]) => {
    try {
      apply(await api(url));
    } catch (error) {
      console.error(name, error);
      failed.push({ name, message: error.message });
    }
  }));

  state.loading = false;
  setUi({ refreshing: false, failedLoads: failed.map(item => item.name) });
  if (!silent) {
    const [first] = failed;
    if (!first) toast("資料已更新");
    // 沒登入就是沒登入，每一支都會失敗，不用講是哪一支
    else toast(state.ui.loginVisible ? first.message : `讀不到 ${first.name}：${first.message}`);
  }

  // 同步狀態是設定頁的東西，監督者沒有那一頁也沒有那個權限——
  // 不先問過清單就會固定收到一個 403
  if (can(state.role, "GET", "/api/status")) void refreshSyncStatus();
  void loadSeason1();
  void checkForUpdate();
}

/**
 * 第一季的紀錄。已封存不會再變，所以成功一次就不再抓；回到前景的自動重抓因此
 * 不必多打兩支 API。失敗就算了，下一次 loadAll 再試，不打擾使用者。
 */
async function loadSeason1() {
  if (state.season1Loaded) return;
  try {
    const range = `start=${SEASON_1.start}&end=${SEASON_1.end}`;
    const [l, h] = await Promise.all([
      api(`/api/daily-logs?${range}`),
      api(`/api/daily-health?${range}`)
    ]);
    state.season1Logs = Object.fromEntries((l.logs || []).map(row => [row.log_date, normalizeLog(row)]));
    state.season1HealthMetrics = Object.fromEntries((h.metrics || []).map(row => [row.log_date, normalizeHealthMetric(row)]));
    state.season1Loaded = true;
    rerender();
  } catch (error) {
    console.warn(error);
  }
}

async function refreshSyncStatus() {
  try {
    setUi({ syncStatus: await api("/api/status"), syncError: undefined });
  } catch (error) {
    setUi({ syncStatus: undefined, syncError: error.message });
  }
}

function shiftMonth(key, delta) {
  const [year, month] = key.split("-").map(Number);
  const date = new Date(year, month - 1 + delta, 1, 12);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

function clampCalendarMonth(key) {
  const min = PLAN_START.slice(0, 7);
  const max = PLAN_END.slice(0, 7);
  return key < min ? min : key > max ? max : key;
}

function downloadCsv() {
  const headers = [
    "date", "measured_at", "weight_kg", "body_fat_percent", "lean_body_mass_kg", "bmi",
    ...BOOL_FIELDS, "steps_count", "note"
  ];
  // 匯出只有本人有，而本人永遠是 revealed——遮蔽分支拿不到公斤，匯出一份
  // 無單位的 0–1 沒有任何用途，所以那時直接給空的。
  const measurements = state.view.kind === "revealed" ? state.view.rows : [];
  const byDate = Object.fromEntries(measurements.map(m => [m.measurement_date, m]));
  const dates = [...new Set([
    ...measurements.map(m => m.measurement_date),
    ...Object.keys(dailyLogs.logs),
    ...Object.keys(state.healthMetrics)
  ])].sort();

  const rows = dates.map(date => {
    const measurement = byDate[date] || {};
    const log = dailyLogs.logs[date] || {};
    const health = state.healthMetrics[date] || {};
    return [
      date, measurement.measured_at, measurement.weight_kg, measurement.body_fat_percent,
      measurement.lean_body_mass_kg, measurement.bmi,
      ...BOOL_FIELDS.map(field => (log[field] ? 1 : 0)),
      health.steps_count, log.note || ""
    ];
  });

  const csv = [headers, ...rows].map(row => row.map(csvEscape).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = `fight-health-${todayIso()}.csv`;
  link.click();
  URL.revokeObjectURL(link.href);
}

const actions = {
  onTabChange(tab) {
    setUi({ activeTab: tab });
    window.scrollTo({ top: 0, behavior: "smooth" });
    if (tab === "settings") void refreshSyncStatus();
  },

  async onLogin(password) {
    setUi({ loginError: "" });
    try {
      await api("/api/auth/login", { method: "POST", body: JSON.stringify({ password }) });
      await readRole();
      setUi({ loginVisible: false });
      await loadAll();
    } catch (error) {
      setUi({ loginError: error.message });
    }
  },

  async onLogout() {
    await fetch("/api/auth/logout", { method: "POST", credentials: "same-origin" });
    state.role = null;
    setUi({ loginVisible: true, activeTab: "today", installHint: null });
  },

  onRefresh() { void loadAll(); },
  onToggleTask(date, field, next) { void dailyLogs.toggle(date, field, next); },
  onNote(date, text) { dailyLogs.setNote(date, text); },

  onSelectDate(date) {
    setUi({ selectedDate: date });
    if (matchMedia("(max-width:600px)").matches) {
      document.querySelector("#selectedDayEditor")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  },

  onCalendarShift(delta) {
    const month = clampCalendarMonth(shiftMonth(state.ui.calendarMonth, delta));
    setUi({ calendarMonth: month, selectedDate: clampToPlan(`${month}-01`) });
  },

  onCalendarToday() {
    setUi({ calendarMonth: planDate().slice(0, 7), selectedDate: planDate() });
  },

  onOpenCalendar() {
    setUi({ activeTab: "calendar", calendarMonth: planDate().slice(0, 7), selectedDate: planDate() });
    window.scrollTo({ top: 0, behavior: "smooth" });
  },

  /** 展開／收起某一天的補登面板。再點一次同一個就收起來。 */
  onToggleBackfill(kind, date) {
    const open = state.ui.openBackfill;
    const same = open && open.kind === kind && open.date === date;
    setUi({ openBackfill: same ? null : { kind, date } });
  },

  /** 跳到某一天的月曆。與 onOpenCalendar 同一件事，只差日期不是今天。 */
  onGoToDate(date) {
    setUi({ activeTab: "calendar", calendarMonth: date.slice(0, 7), selectedDate: date });
    window.scrollTo({ top: 0, behavior: "smooth" });
  },

  /** 打字。status 回 idle 並清掉錯誤——上一次失敗的訊息不該賴在新的輸入上。 */
  onBackfillSteps(date, value) {
    setUi({
      backfill: {
        ...state.ui.backfill,
        steps: { date, value, status: "idle", error: "" }
      }
    });
  },

  /**
   * 打字（身體數值）。換一天就從空白開始——留著上一天的草稿，畫面上那幾格
   * 會看起來像那一天已經有值。
   */
  onBackfillBody(date, field, value) {
    const current = state.ui.backfill.body;
    const base = current.date === date
      ? current
      : { date, weight: "", fat: "", lean: "", bmi: "" };
    setUi({
      backfill: {
        ...state.ui.backfill,
        body: { ...base, date, [field]: value, status: "idle", error: "" }
      }
    });
  },

  /**
   * 送出步數。
   *
   * **刻意沒有樂觀更新。** 數字的回滾比打勾可怕——打 8432、失敗後畫面自己
   * 變回舊值，看起來像 app 竄改資料。所以先送出、成功了才動畫面。
   */
  async onSaveSteps(date) {
    const draft = state.ui.backfill.steps;
    const steps = Number(draft.value);
    if (!Number.isFinite(steps)) return;

    setUi({ backfill: { ...state.ui.backfill, steps: { ...draft, status: "saving", error: "" } } });
    try {
      const data = await api("/api/daily-health", {
        method: "PUT",
        body: JSON.stringify({ log_date: date, steps_count: steps })
      });
      /*
       * 就地更新，**不呼叫 loadAll()**：回應已經帶回整列了，而 loadAll() 的
       * state.loading 併發保護會讓儲存流程出現「有時候沒更新」的間歇行為。
       */
      state.healthMetrics[date] = normalizeHealthMetric(data.metric);
      /*
       * 草稿清掉（date 回 null），輸入框的值於是回到 props——畫面上的數字、
       * 進度條與 Rank 一起變才是真正的回饋。
       *
       * `savedDate` 與 `date` 分開是必要的：「已儲存」要掛在正確的那一天上，
       * 但草稿本身必須讓開，否則輸入框會停在空字串而不是新值。
       */
      /*
       * 面板收起來，成功用 toast 講。
       *
       * 先前「已儲存」是印在面板裡的一行字——面板一收，那行字就沒地方待了。
       * toast 是這個 codebase 既有的「做完了」管道（代謝設定、暱稱都走它）。
       * 畫面上的數字、進度條與 Rank 一起變仍然是主要的回饋，toast 只是補一句。
       */
      setUi({
        openBackfill: null,
        backfill: {
          ...state.ui.backfill,
          steps: { date: null, value: "", status: "idle", error: "" }
        }
      });
      toast(`${date} 的步數已補登`);
    } catch (error) {
      setUi({
        backfill: {
          ...state.ui.backfill,
          // 草稿原樣留著：使用者打的字比「存不起來」更不該被動到
          steps: { ...state.ui.backfill.steps, status: "error", error: error.message }
        }
      });
    }
  },

  /**
   * 送出身體數值。**四格一次送一整列**，不逐格儲存。
   *
   * 逐格會產生四個請求而且可能部分成功，那是最難跟使用者解釋的狀態——尤其
   * 這四個數字彼此有推導關係，體重存進去了但體脂沒有，資料庫裡就留下一組
   * 互相矛盾的值。整列寫入同時也讓 source 說得清楚（見 docs/adr/0006）。
   */
  async onSaveBody(date, payload) {
    const draft = state.ui.backfill.body;
    setUi({ backfill: { ...state.ui.backfill, body: { ...draft, status: "saving", error: "" } } });
    try {
      const data = await api("/api/measurements", {
        method: "PUT",
        body: JSON.stringify({ measurement_date: date, ...payload })
      });

      /*
       * view 是個 union，塞不進單獨一列，所以把新的那列併進原始 rows 之後
       * 重建一次。本人永遠是 revealed——補登的入口只有他有。
       */
      const rows = state.view.kind === "revealed" ? state.view.rows : [];
      const merged = rows.filter(row => row.measurement_date !== date)
        .concat(normalizeMeasurement(data.measurement))
        .sort((a, b) => a.measurement_date.localeCompare(b.measurement_date));
      state.view = measurementView(merged, false, null);

      setUi({
        openBackfill: null,
        backfill: {
          ...state.ui.backfill,
          body: {
            date: null, weight: "", fat: "", lean: "", bmi: "",
            status: "idle", error: ""
          }
        }
      });
      toast(`${date} 的量測已補登`);
    } catch (error) {
      setUi({
        backfill: {
          ...state.ui.backfill,
          body: { ...state.ui.backfill.body, status: "error", error: error.message }
        }
      });
    }
  },

  onMascotClick() {
    const next = state.ui.npcOffset + 1;
    writeNpcLineOffset(next, planDate());
    // 先移除再加上，否則同一個動畫不會重播
    setUi({ npcOffset: next, mascotBouncing: false });
    requestAnimationFrame(() => setUi({ mascotBouncing: true }));
  },

  onMascotAnimationEnd() { setUi({ mascotBouncing: false }); },

  onSaveProfile(raw) {
    state.profile = normalizeProfile(raw);
    saveEnergyProfile(state.profile);
    rerender();
    toast("代謝設定已更新");
  },

  /** 還沒有名字就想戳：切到戳戳樂分頁。捲動與聚焦由 src/ui/ 做，這裡不碰 DOM。 */
  onNeedNickname() {
    setUi({ activeTab: "cheer" });
  },

  onSaveNickname(raw) {
    const nickname = normalizeNickname(raw);
    if (!nickname) {
      // 前端擋是為了當場說明，後端擋才是真的擋——兩邊呼叫的是同一個函式
      setUi({ nicknameError: "只能用中英文與數字，長度上限 4 個中文字或 8 個英文字母。" });
      return;
    }
    saveNickname(nickname);
    setUi({ nickname, nicknameError: "" });
    toast("名字已存起來");
  },

  // 失敗進 toast：戳沒有自己的錯誤區，而且它只是「沒送出去」，不是填錯東西
  async onCheer(date) {
    /*
     * **先立旗標再送出，順序不能反。**
     *
     * `cheers.cheer()` 的樂觀更新是同步的——呼叫下去的那一刻招呼卡就換成
     * 回條了。旗標如果等 `await` 回來才設，回條的第一幀是「已經變身完成」
     * 的樣子（應援版、萊姆綠），下一幀才被 morphOut 拉回舉重版重跑一次動畫。
     * 畫面上就是：卡片先變 → 圖片閃一下 → 才開始蹲跳。那一閃就是那一幀。
     *
     * 也不需要「先關再開」重新觸發動畫：回條是這時候才掛上去的新元素，
     * 動畫本來就從頭播；那一招是給 onMascotClick 用的（那隻肉羹一直都在）。
     */
    setUi({ cheerBouncing: true });

    const result = await cheers.cheer(date, state.ui.nickname);
    if (!result.ok) {
      toast(result.error);
      setUi({ cheerBouncing: false });
    }
  },

  onCheerAnimationEnd() { setUi({ cheerBouncing: false }); },

  // 失敗留在競猜卡片上：那裡才說得出「為什麼不能改」，toast 兩秒就沒了
  async onBet({ will_finish, will_reach_weight, weight_kg, finish_time }) {
    const rawTime = finish_time.trim();
    const finishSeconds = rawTime ? parseFinishTime(rawTime) : null;
    if (rawTime && !validFinishSeconds(finishSeconds)) {
      setUi({ predictionError: `${FINISH_RANGE_MESSAGE}，官方限時內才算完賽。` });
      return;
    }
    setUi({ predictionError: "" });
    const result = await racePredictions.bet(
      state.ui.nickname,
      { willFinish: will_finish, willReachWeight: will_reach_weight, weightKg: weight_kg, finishSeconds },
      todayIso()
    );
    if (result.ok) toast("猜測已送出");
    else setUi({ predictionError: result.error });
  },

  /**
   * 官方完賽成績。**刻意沒有樂觀更新**，理由與步數補登相同：先送出、成功了才動畫面，
   * 不然失敗回滾看起來像 app 竄改資料。
   */
  async onSaveRaceResult({ outcome, finish_time }) {
    const finished = outcome === "finished";
    const finishSeconds = finished ? parseFinishTime(finish_time) : null;
    if (finished && finishSeconds === null) {
      setUi({ raceResultError: "完賽要填官方時間，例如 2 時 35 分 20 秒。" });
      return;
    }
    if (finished && !validFinishSeconds(finishSeconds)) {
      setUi({ raceResultError: `${FINISH_RANGE_MESSAGE}。超過官方限時請選未完賽。` });
      return;
    }
    setUi({ raceResultError: "" });
    try {
      const data = await api("/api/race-result", {
        method: "PUT",
        body: JSON.stringify({ finish_seconds: finishSeconds, cancelled: outcome === "cancelled" })
      });
      state.raceResult = data.result;
      rerender();
      toast("完賽成績已存起來");
    } catch (error) {
      setUi({ raceResultError: error.message });
    }
  },

  onRevealSeen(id) {
    const seen = [...state.ui.seenReveals, id];
    saveSeenReveals(seen);
    setUi({ seenReveals: seen });
  },
  onDismissInstallHint() {
    dismissInstallHint();
    setUi({ installHint: null });
  },

  onDismissNotice(id) {
    dismissNotice(id);
    setUi({ notice: currentNotice(id) });
  },

  onReload() { location.reload(); },

  onRecheckSync() { void refreshSyncStatus(); },
  onDownloadCsv() { downloadCsv(); },
  onPrint() { window.print(); },

  async onCopySummary() {
    await navigator.clipboard.writeText(summaryTextFor({
      // 文字戰報同樣只有本人有，理由與 downloadCsv 相同
      measurements: state.view.kind === "revealed" ? state.view.rows : [],
      logs: dailyLogs.logs,
      healthMetrics: state.healthMetrics,
      today: planDate(),
      profile: state.profile,
      baseline: seasonBaseline(state.season1Measurements)
    }));
    toast("摘要已複製");
  },

  onToggleHelp(label) {
    setUi({ openHelp: state.ui.openHelp === label ? null : label });
  },

  onToggleAchievement(id) {
    setUi({ openAchievement: state.ui.openAchievement === id ? null : id });
  },

  // 通知權限的詢問必須留在使用者手勢的呼叫堆疊裡，所以只能由這個開關觸發
  async onToggleBadge(next) {
    setUi({ badge: await setBadgeEnabled(next) });
  }
};

/**
 * 手機底部選單隨捲動連續收合。
 *
 * 這裡只算一個 0→1 的進度：頂端是 0，往下滑到 SHRINK_RANGE 之後停在 1。
 * 整體縮放與文字淡出都由 CSS 依這個進度推導，所以兩者永遠同步，
 * 而且是連續的，不會有突然變小的跳動。
 *
 * 直接寫 CSS 變數而不是走 setUi()：這是每個影格都會變的樣式值，
 * 讓整棵 vdom 每幀重繪一次只是白費效能。內容仍然完全由 Preact 管，
 * 這裡碰的只是一個數字。
 */
const SHRINK_RANGE = 220;

function bindTabShrink() {
  const reduceMotion = matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (reduceMotion) return;

  let ticking = false;
  const apply = () => {
    ticking = false;
    const progress = Math.min(1, Math.max(0, window.scrollY / SHRINK_RANGE));
    document.documentElement.style.setProperty("--tabs-shrink", progress.toFixed(4));
  };

  window.addEventListener("scroll", () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(apply);
  }, { passive: true });

  apply();
}

function bindGlobal() {
  // 點說明按鈕以外的地方就收起 tooltip。按鈕本身會 stopPropagation。
  document.addEventListener("click", () => {
    if (state.ui.openHelp !== null) setUi({ openHelp: null });
  });
  document.addEventListener("keydown", event => {
    if (event.key === "Escape" && state.ui.openHelp !== null) setUi({ openHelp: null });
  });

  bindTabShrink();

  // iOS 從主畫面切回 PWA 時是恢復凍結的頁面而不是重新載入，
  // 所以啟動時的 loadAll 不會再跑一次。沒有這個監聽器就會一直看到舊資料。
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState !== "visible") return;
    if (!state.role) return;

    // 版本檢查不受資料重抓的節流限制：那是一支很小的檔案，而「我放著的時候
    // 是不是有新版」正是切回來時要知道的事。
    void checkForUpdate();

    if (Date.now() - state.lastLoadedAt < FOREGROUND_REFRESH_INTERVAL) return;
    void loadAll({ silent: true });
  });

  if ("serviceWorker" in navigator) navigator.serviceWorker.register("/sw.js").catch(console.warn);
}

/**
 * 第二季開始前交給封存版，11/4 的結算由原本那份程式執行，見 docs/adr/0007。
 *
 * 用 replace 不留歷史，否則按返回會回到這裡又被轉走。先註冊 service worker 再
 * 轉：這樣新版會先裝好，11/5 打開就是第二季。切換完成後這段可以移除。
 */
if (todayIso() < PLAN_START) {
  if ("serviceWorker" in navigator) navigator.serviceWorker.register("/sw.js").catch(console.warn);
  location.replace("/1.0/");
} else {
  rerender();
  bindGlobal();
  void checkSession();
}
